<template>
  <div class="metrics-drill-down">
    <!-- Section header -->
    <div class="metrics-drill-down__header">
      <div class="metrics-drill-down__title-wrap">
        <span class="metrics-drill-down__title heading-s">{{
          $t("metrics.drillDownTitle")
        }}</span>
        <!-- <eg-icon icon="information" class="metrics-drill-down__info-icon" /> -->
        <p class="metrics-drill-down__subtitle paragraph-m">
          {{ $t("metrics.drillDownSubtitle") }}
        </p>
      </div>

      <div class="metrics-drill-down__search">
        <eg-input
          v-model="searchQuery"
          :placeholder="$t('metrics.searchClass')"
          variant="search"
          type="search"
          size="medium"
        />
      </div>
    </div>

    <!-- Source tabs (always visible once we know tabs exist) -->
    <div v-if="visibleTabs.length > 0" class="metrics-drill-down__tabs">
      <button
        v-for="tab in visibleTabs"
        :key="tab.key"
        class="metrics-drill-down__tab"
        :class="{
          'metrics-drill-down__tab--active': activeSource === tab.key,
        }"
        @click="activeSource = tab.key"
      >
        {{ tab.label }}
      </button>
    </div>

    <!-- Loading state -->
    <div
      v-if="isLoading && displayedClasses.length === 0"
      class="metrics-drill-down__loader"
    >
      <infinity-loading />
    </div>

    <!-- No classes at all (only when "all" tab active and no search) -->
    <metrics-empty-state
      v-else-if="
        !isLoading &&
        totalClasses === 0 &&
        activeSource === 'all' &&
        !searchQuery.trim()
      "
      :title="$t('metrics.noClasses')"
      :description="$t('metrics.noClassesDescription')"
    />

    <!-- Table (eg-pv-table shows its own empty-text when items are empty) -->
    <template v-else>
      <div class="metrics-drill-down__table-wrapper">
        <eg-pv-table
          :key="tableKey"
          :items="displayedClasses"
          :fields="tableFields"
          :empty-text="$t('metrics.noClassesFound')"
          class="metrics-drill-down__table"
          @sort="handleSort"
          @row-click="navigateToClass"
        >
          <template #cell(name)="{ value }">
            <span class="metrics-drill-down__class-name paragraph-s">{{
              value
            }}</span>
          </template>

          <template #cell(source)="{ value }">
            <span class="metrics-drill-down__source paragraph-s">{{
              sourceLabel(value)
            }}</span>
          </template>

          <template #cell(avgScore)="{ value }">
            <span class="metrics-drill-down__score heading-xs">{{
              value != null ? value + "%" : "—"
            }}</span>
          </template>

          <template #cell(trend)="{ item }">
            <metrics-trend-sparkline :data="item.trend" />
          </template>

          <template #cell(students)="{ value }">
            <span class="paragraph-s">{{ value != null ? value : "—" }}</span>
          </template>

          <template #cell(needSupport)="{ value }">
            <span class="paragraph-s">{{ value != null ? value : "—" }}</span>
          </template>

          <template #cell(actions)="{ item }">
            <eg-button
              size="small"
              class="metrics-drill-down__view-btn"
              :to="`${classBasePath}/class/${item.id}`"
              @click="$emit('view-class', item)"
            >
              {{ $t("metrics.view") }}
            </eg-button>
          </template>
        </eg-pv-table>
      </div>

      <!-- Pagination -->
      <metrics-pagination
        :value="currentPage"
        :total-rows="totalClasses"
        :per-page="pageSize"
        @change="onPageChange"
      >
        <template #label>
          {{
            $t("metrics.drillDownShowing", {
              count: showingCount,
              total: totalClasses,
            })
          }}
        </template>
      </metrics-pagination>
    </template>
  </div>
</template>

<script>
import { METRICS_SOURCES } from "../../../demo_data/shared.js";
import { metricsOverviewDemoData } from "../../../demo_data/overview.js";
import MetricsEmptyState from "@/views/metrics/components/shared/metrics_empty_state";
import InfinityLoading from "@/components/rubrics/newRubric/components/infinity-loading.vue";
import MetricsTrendSparkline from "./metrics_trend_sparkline.vue";
import { getMetricsClassesDrillDown } from "@/api/requests/metrics.js";
import debounce from "@/utils/debounce.js";
import MetricsPagination from "@/views/metrics/components/shared/metrics_pagination.vue";

// Ordered list of platform tabs with their createGrade keys.
// A tab is shown when the platform is enabled in config/features.createGrade.
const PLATFORM_TAB_CONFIGS = [
  { key: "essaygrader", label: "EssayGrader", createGradeKeys: ["upload"] },
  {
    key: "google_classroom",
    label: "Google Classroom",
    createGradeKeys: ["google"],
  },
  {
    key: "canvas",
    label: "Canvas",
    createGradeKeys: ["canvas", "canvas-manual"],
  },
  { key: "schoology", label: "Schoology", createGradeKeys: ["schoology"] },
];

// Map API platform values to internal source keys used in tabs/filters.
const PLATFORM_TO_SOURCE = {
  google: "google_classroom",
  canvas: "canvas",
  essaygrader: "essaygrader",
  schoology: "schoology",
};

// Map internal source keys to API platform values.
const SOURCE_TO_PLATFORM = {
  google_classroom: "google",
  canvas: "canvas",
  essaygrader: "essaygrader",
  schoology: "schoology",
};

function normalizeClass(item) {
  return {
    id: item.id,
    name: item.name,
    source: PLATFORM_TO_SOURCE[item.platform] || item.platform,
    avgScore: item.avgScore,
    trend: item.trend,
    students: item.studentsCount,
    needSupport: item.needSupportCount,
  };
}

export default {
  name: "MetricsDrillDown",
  components: {
    MetricsEmptyState,
    InfinityLoading,
    MetricsTrendSparkline,
    MetricsPagination,
  },
  props: {
    isDemoMode: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    dateFrom: {
      type: Number,
      default: null,
    },
    dateTo: {
      type: Number,
      default: null,
    },
    classBasePath: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      searchQuery: "",
      activeSource: "all",
      sortField: "name",
      sortOrder: "asc",
      classes: [],
      total: 0,
      currentPage: 1,
      pageSize: 10,
      tableKey: 0,
      isLoading: !this.isDemoMode && !this.disabled,
    };
  },

  computed: {
    createGrade() {
      return this.$store.getters["config/features"]?.createGrade || [];
    },

    tableFields() {
      return [
        {
          key: "name",
          label: this.$t("metrics.tableClassName"),
          sortable: true,
        },
        {
          key: "source",
          label: this.$t("metrics.tableSource"),
          style: "width: 18%",
        },
        {
          key: "avgScore",
          label: this.$t("metrics.tableAvgScore"),
          style: "width: 12%",
        },
        {
          key: "trend",
          label: this.$t("metrics.tableTrend"),
          style: "width: 18%",
        },
        {
          key: "students",
          label: this.$t("metrics.students"),
          style: "width: 10%",
        },
        {
          key: "needSupport",
          label: this.$t("metrics.tableNeedSupport"),
          style: "width: 12%",
        },
        {
          key: "actions",
          label: this.$t("metrics.tableAction"),
          style: "width: 10%",
        },
      ];
    },

    // In demo mode — show ALL platform tabs unconditionally.
    // In real mode — show only platforms enabled in createGrade.
    // Does NOT depend on whether classes exist for a given platform;
    // ALL tabs are hidden only when totalClasses === 0 (handled in template).
    visibleTabs() {
      const platformTabs = this.isDemoMode
        ? PLATFORM_TAB_CONFIGS
        : PLATFORM_TAB_CONFIGS.filter((tab) =>
            tab.createGradeKeys.some((k) => this.createGrade.includes(k))
          );

      if (platformTabs.length === 0) return [];

      // "Show All" only makes sense when more than one platform tab is visible
      if (platformTabs.length > 1) {
        return [
          { key: "all", label: this.$t("metrics.showAll") },
          ...platformTabs,
        ];
      }

      return platformTabs;
    },

    filteredDemoClasses() {
      if (!this.isDemoMode) return [];

      let result = [...metricsOverviewDemoData.classes];

      if (this.activeSource !== "all") {
        result = result.filter((c) => c.source === this.activeSource);
      }

      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase();
        result = result.filter((c) => c.name.toLowerCase().includes(q));
      }

      if (this.sortField) {
        const dir = this.sortOrder === "desc" ? -1 : 1;
        result.sort((a, b) => {
          const aVal = a[this.sortField];
          const bVal = b[this.sortField];
          if (aVal === bVal) return 0;
          return (aVal < bVal ? -1 : 1) * dir;
        });
      }

      return result;
    },

    displayedClasses() {
      if (this.isDemoMode) {
        const start = (this.currentPage - 1) * this.pageSize;
        return this.filteredDemoClasses.slice(start, start + this.pageSize);
      }
      return this.classes;
    },

    totalClasses() {
      return this.isDemoMode ? this.filteredDemoClasses.length : this.total;
    },

    showingCount() {
      return this.displayedClasses.length;
    },

    dateRangeKey() {
      return `${this.dateFrom}_${this.dateTo}`;
    },
  },

  watch: {
    isDemoMode(newVal) {
      this.currentPage = 1;
      if (newVal) {
        this.classes = [];
        this.total = 0;
        this.isLoading = false;
      } else if (!this.disabled) {
        this.fetchClasses();
      }
    },

    disabled(val) {
      if (!val && !this.isDemoMode) this.fetchClasses();
    },

    dateRangeKey(newVal, oldVal) {
      if (newVal === oldVal || this.isDemoMode || this.disabled) return;
      this.currentPage = 1;
      this.fetchClasses();
    },

    activeSource(newVal, oldVal) {
      if (newVal === oldVal) return;
      this.currentPage = 1;
      if (!this.isDemoMode && !this.disabled) this.fetchClasses();
    },

    searchQuery() {
      if (this.isDemoMode || this.disabled) return;
      this.currentPage = 1;
      this.debouncedFetch();
    },

    visibleTabs(tabs) {
      const isActiveValid = tabs.some((t) => t.key === this.activeSource);
      if (!isActiveValid) {
        this.activeSource = tabs.length > 0 ? tabs[0].key : "all";
      }
    },
  },

  created() {
    this.debouncedFetch = debounce(this.fetchClasses, 400);
    if (!this.isDemoMode && !this.disabled) this.fetchClasses();
  },

  methods: {
    sourceLabel(sourceKey) {
      return METRICS_SOURCES[sourceKey] || sourceKey;
    },

    handleSort({ field, order }) {
      this.sortField = field;
      this.sortOrder = order;
      if (!this.isDemoMode) {
        this.currentPage = 1;
        this.fetchClasses();
      }
    },

    onPageChange(page) {
      this.currentPage = page;
      this.fetchClasses();
    },

    navigateToClass(item) {
      this.$router.push(`${this.classBasePath}/class/${item.id}`);
    },

    async fetchClasses() {
      if (this.isDemoMode) return;
      this.isLoading = true;

      const platform =
        this.activeSource !== "all"
          ? SOURCE_TO_PLATFORM[this.activeSource]
          : undefined;

      try {
        const { orgId, userId } = this.$route.params;
        const entityId = orgId ? userId || orgId : this.$getClerk()?.user?.id;
        const res = await getMetricsClassesDrillDown({
          entityId,
          dateFrom: this.dateFrom,
          dateTo: this.dateTo,
          page: this.currentPage,
          limit: this.pageSize,
          ...(platform && { platform }),
          ...(this.searchQuery.trim() && { search: this.searchQuery.trim() }),
          ...(this.sortField && {
            sortBy: this.sortField,
            sortOrder: this.sortOrder,
          }),
        });
        this.classes = (res.data.data || []).map(normalizeClass);
        this.total = res.data.total || 0;
        this.tableKey++;
      } catch {
        this.classes = [];
        this.total = 0;
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.metrics-drill-down {
  background: var(--color-surface-neutral-background, #fff);
  border: 1px solid var(--color-stroke-neutral-default, #e5ebf0);
  border-radius: 12px;
  overflow: hidden;

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 20px 24px 0;
    flex-wrap: wrap;

    @media (max-width: 1024px) {
      padding: 16px 16px 0;
    }
  }

  &__title-wrap {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
  }

  &__title {
    font-size: var(--typography-font-size-fs-600);
  }

  &__info-icon {
    color: var(--color-icon-neutral-secondary, #8a97a4);
    font-size: 14px;
    cursor: help;
  }

  &__subtitle {
    flex-basis: 100%;
    margin: 0;
    font-size: var(--typography-font-size-fs-400);
    font-weight: var(--typography-font-weight-fw-400, 400);
  }

  &__search {
    flex-shrink: 0;
    width: 220px;

    @media (max-width: 1024px) {
      width: 100%;
    }
  }
  &__view-btn {
    font-size: var(--typography-font-size-fs-400);
    line-height: 11px;
  }
  &__tabs {
    display: flex;
    gap: 0;
    padding: 12px 24px 0;
    border-bottom: 1px solid var(--color-stroke-neutral-default, #e5ebf0);
    overflow-x: auto;
    margin-bottom: 24px;

    &::-webkit-scrollbar {
      display: none;
    }

    @media (max-width: 1024px) {
      padding: 12px 16px 0;
    }
  }

  &__tab {
    padding: 8px 16px;
    border: none;
    background: transparent;
    cursor: pointer;
    position: relative;
    color: var(--color-text-neutral-fade, #8a97a4);
    font-family: var(--typography-font-family-text-primary, Inter);
    font-size: var(--typography-font-size-fs-300, 14px);
    font-weight: var(--typography-font-weight-fw-500, 500);
    line-height: 150%;
    transition: color 0.15s ease;
    white-space: nowrap;

    &:hover {
      color: var(--color-text-primary-default, #7c3aed);
    }

    &--active {
      color: var(--color-text-primary-default, #7c3aed);

      &::after {
        content: "";
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: var(--color-stroke-primary-default, #7c3aed);
        border-radius: 2px 2px 0 0;
      }
    }
  }

  &__class-name {
    color: var(--color-text-neutral-on-white, #1a2330);
  }

  &__score {
    font-weight: var(--typography-font-weight-fw-700, 700);
    color: var(--color-text-neutral-on-white, #1a2330);
  }

  &__table-wrapper {
    overflow-x: auto;
    min-width: 0;
    padding: 0 24px;
  }

  &__table {
    ::v-deep .p-datatable-tbody > tr {
      cursor: pointer;
    }
    ::v-deep
      .p-datatable-thead
      th:not(:first-of-type)
      .p-column-header-content {
      justify-content: center;
    }
    ::v-deep .p-datatable-tbody > tr td:not(:first-of-type) {
      text-align: center;
    }
    ::v-deep .p-datatable-thead th:first-child,
    ::v-deep .p-datatable-tbody > tr td:first-child {
      width: 250px;
      min-width: 250px;
    }
    ::v-deep .p-datatable-thead th:nth-child(2) {
      width: 160px;
      min-width: 160px;
    }
    ::v-deep .p-datatable-thead th:nth-child(4) {
      min-width: 150px;
    }
  }

  &__loader {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 24px;
  }
}
</style>
